import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AreaChart, Area, ResponsiveContainer, XAxis, Tooltip } from "recharts";
import client from "@/lib/apiClient";
import { useLang } from "@/i18n";
import { useProperty } from "@/context/PropertyContext";
import { SectionHeader, Loader, Lu, fmtSAR } from "@/components/shared";
import { LogoMark } from "@/components/Brand";
import { Button } from "@/components/ui/button";

export default function Digest() {
  const { lang } = useLang();
  const { selectedId } = useProperty();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const L = lang === "ar";

  const load = () => client.get(`/properties/${selectedId}/digest`).then((r) => setD(r.data));
  useEffect(() => { if (selectedId) { setD(null); load(); } }, [selectedId]); // eslint-disable-line

  const sendTest = async () => {
    setBusy(true);
    try {
      const r = await client.post(`/properties/${selectedId}/digest/send-test`);
      toast.success(L ? `تم وضع الرسالة في قائمة الإرسال (محاكاة) إلى ${r.data.to.join(", ")}` : `Queued (mock) to ${r.data.to.join(", ")}`);
      load();
    } catch { toast.error(L ? "فشل الإرسال" : "Send failed"); } finally { setBusy(false); }
  };

  if (!d) return <Loader />;
  const k = d.kpis;
  const cards = [
    { icon: "zap", label: L ? "كيلوواط موفّرة" : "kWh saved", value: fmtSAR(k.saved_kwh), c: "#1E5EFF" },
    { icon: "wallet", label: L ? "ريال موفّر" : "SAR saved", value: fmtSAR(k.saved_sar), c: "#00A69E" },
    { icon: "trending-down", label: L ? "متوسط التوفير" : "Avg savings", value: `${k.avg_savings_pct}%`, c: "#10B981" },
    { icon: "users-round", label: L ? "الإشغال" : "Occupancy", value: `${k.occupancy_pct}%`, c: "#8B5CF6" },
    { icon: "bell-ring", label: L ? "تنبيهات محلولة" : "Alerts resolved", value: `${k.alerts_resolved}/${k.alerts_total}`, c: "#F59E0B" },
    { icon: "ticket", label: L ? "تذاكر مفتوحة" : "Open tickets", value: k.tickets_open, c: "#F43F5E" },
  ];

  return (
    <div>
      <SectionHeader icon="mail" title={L ? "الملخص الأسبوعي" : "Weekly Digest"} subtitle={L ? `معاينة البريد الأسبوعي للملاك · ${d.schedule}` : `Owner weekly email preview · ${d.schedule}`}>
        <div className="flex items-center gap-3 flex-wrap">
          <span data-testid="digest-mock-badge" className="text-xs px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30 font-semibold">
            {L ? "الإرسال الفعلي غير مفعّل (محاكاة)" : "Email delivery MOCKED"}
          </span>
          <Button data-testid="digest-send-test" onClick={sendTest} disabled={busy} className="md-gold-gradient text-white font-bold border-0">
            <Lu name="send" className="w-4 h-4 ltr:mr-2 rtl:ml-2" />{L ? "إرسال تجريبي" : "Send test"}
          </Button>
        </div>
      </SectionHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Email preview */}
        <div className="lg:col-span-2 md-card overflow-hidden" data-testid="digest-preview">
          <div className="px-5 py-3 border-b border-border bg-foreground/[0.03] text-xs text-muted-foreground space-y-0.5">
            <p><span className="font-semibold text-foreground/80">{L ? "من:" : "From:"}</span> Madarek Smart &lt;info@smartmadarek.com&gt;</p>
            <p><span className="font-semibold text-foreground/80">{L ? "إلى:" : "To:"}</span> {d.to.join(", ")}</p>
            <p><span className="font-semibold text-foreground/80">{L ? "الموضوع:" : "Subject:"}</span> {L ? `ملخص مدارك الأسبوعي — ${d.property.name_ar} — ${d.week_ending}` : `Madarek Weekly Digest — ${d.property.name_en} — ${d.week_ending}`}</p>
          </div>
          <div className="p-6 bg-white text-[#0B1A4A]" dir={L ? "rtl" : "ltr"}>
            <div className="flex items-center gap-3 mb-6">
              <LogoMark size={40} />
              <div><p className="font-extrabold tracking-[0.18em] leading-none">MADAREK</p><p className="text-[10px] font-semibold tracking-[0.3em] text-[#00857D] mt-1">SMART</p></div>
            </div>
            <h2 className="text-2xl font-extrabold">{L ? `أسبوعك في ${d.property.name_ar}` : `Your week at ${d.property.name_en}`}</h2>
            <p className="text-sm text-slate-600 mt-1">{L ? "إليك ما حققته طبقة ذكاء مدارك هذا الأسبوع." : "Here is what the Madarek Intelligence Layer achieved this week."}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-5">
              {cards.map((c) => (
                <div key={c.label} className="rounded-xl border border-slate-200 p-3">
                  <div className="flex items-center gap-2 text-xs text-slate-500"><Lu name={c.icon} className="w-3.5 h-3.5" style={{ color: c.c }} />{c.label}</div>
                  <p className="text-xl font-extrabold font-mono mt-1" style={{ color: c.c }}>{c.value}</p>
                </div>
              ))}
            </div>
            <div className="mt-5 rounded-xl border border-slate-200 p-3">
              <p className="text-xs font-semibold text-slate-500 mb-2">{L ? "الطاقة: خط الأساس مقابل الفعلي (7 أيام)" : "Energy: baseline vs actual (7 days)"}</p>
              <ResponsiveContainer width="100%" height={120}>
                <AreaChart data={d.series}>
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={(x) => x.slice(5)} />
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Area type="monotone" dataKey="baseline_kwh" stroke="#94A3B8" fill="#E2E8F0" strokeWidth={1.5} />
                  <Area type="monotone" dataKey="actual_kwh" stroke="#0047FF" fill="#0047FF22" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <h3 className="font-bold mt-6 mb-2">{L ? "أهم 3 توصيات من الذكاء الاصطناعي" : "Top 3 AI recommendations"}</h3>
            <div className="space-y-2">
              {d.top_insights.length === 0 && <p className="text-sm text-slate-500">{L ? "افتح لوحة التحكم لتوليد الرؤى أولاً." : "Open the dashboard to generate insights first."}</p>}
              {d.top_insights.map((i, idx) => (
                <div key={idx} className="flex gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <span className="w-6 h-6 rounded-md bg-[#0047FF] text-white text-xs font-bold flex items-center justify-center shrink-0">{idx + 1}</span>
                  <div><p className="text-sm font-semibold">{L ? i.title_ar : i.title_en}</p><p className="text-xs text-slate-600 mt-0.5">{L ? i.detail_ar : i.detail_en}</p>
                    {i.impact_sar_month > 0 && <p className="text-xs font-mono text-emerald-600 mt-1">+{fmtSAR(i.impact_sar_month)} {L ? "ر.س/شهر" : "SAR/mo"}</p>}</div>
                </div>
              ))}
            </div>
            <a href="#" className="inline-block mt-6 px-5 py-2.5 rounded-lg bg-[#0047FF] text-white text-sm font-bold">{L ? "افتح لوحة التحكم" : "Open dashboard"}</a>
            <p className="text-[11px] text-slate-400 mt-6">Madarek Smart Hospitality · info@smartmadarek.com · {L ? "لإلغاء الاشتراك، حدّث الإعدادات" : "Update preferences to unsubscribe"}</p>
          </div>
        </div>

        {/* Delivery settings */}
        <div className="space-y-4">
          <div className="md-card p-5">
            <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2"><Lu name="calendar-clock" className="w-4 h-4 text-primary" />{L ? "الجدولة" : "Schedule"}</h3>
            <p className="text-sm text-foreground/80">{d.schedule}</p>
            <p className="text-xs text-muted-foreground mt-2">{L ? "يُرسل تلقائياً لكل مالك ومدير للعقار. سيتم تفعيل الإرسال الفعلي بعد ربط مزوّد البريد (Resend/SendGrid)." : "Sent automatically to every owner & manager. Real delivery activates once an email provider (Resend/SendGrid) is connected."}</p>
          </div>
          <div className="md-card p-5" data-testid="digest-last-sent">
            <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2"><Lu name="history" className="w-4 h-4 text-primary" />{L ? "آخر إرسال تجريبي" : "Last test send"}</h3>
            {d.last_sent ? (
              <div className="text-sm space-y-1">
                <p className="text-foreground/80">{new Date(d.last_sent.sent_at).toLocaleString(L ? "ar-SA" : "en-GB")}</p>
                <p className="text-xs text-muted-foreground">{d.last_sent.to.join(", ")}</p>
                <span className="inline-block text-[10px] px-2 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400 font-semibold">{d.last_sent.status}</span>
              </div>
            ) : <p className="text-sm text-muted-foreground">{L ? "لم يُرسل بعد" : "Not sent yet"}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
